// COLLECT-FLOWS: one guided collection session per person-role (CEO, account manager, recruiter,
// salesperson, finance & ops). Each question is a short label with an (i) saying why the agents
// need it; its binding names the roles on the work chart that read the answer (`usedBy`).
//
// Answers live at `seat.settings.collect.<key>` of the person's seat (the engine's "real field"
// rule: no side table) and are projected on every save into the seat's role-knowledge doc, linked
// as knowledge to the DEs it names, and into the seat's Recall memory as typed records. Files are
// uploaded to the file store and linked by id, never embedded.
//
// No answer is ever prefilled: a real company starts with empty flows.

import { allSteps, isAnswered } from "./engine";
import type {
  Answers,
  AnswerValue,
  Audience,
  ChapterDef,
  CollectRole,
  FieldDef,
  FieldType,
  FlowDef,
  FlowKey,
  Option,
  StepDef,
} from "./types";

const BOTH: readonly Audience[] = ["assignee", "manager"];

type Q = {
  key: string;
  label: string;
  info: string;
  type: FieldType;
  usedBy: readonly string[];
  required?: boolean;
  decision?: boolean;
  options?: readonly Option[];
  placeholder?: string;
  min?: number;
  max?: number;
};

const q = (x: Q): FieldDef => ({
  key: x.key,
  label: x.label,
  info: x.info,
  type: x.type,
  bind: { kind: "collect", usedBy: x.usedBy, memory: x.decision ? "decision" : "fact" },
  fill: BOTH,
  ...(x.required ? { required: true } : {}),
  ...(x.options ? { options: x.options } : {}),
  ...(x.placeholder ? { placeholder: x.placeholder } : {}),
  ...(x.min !== undefined ? { min: x.min } : {}),
  ...(x.max !== undefined ? { max: x.max } : {}),
  ...(x.type === "longtext" ? { maxLength: 8000 } : {}),
});

const step = (key: string, title: string, info: string, fields: FieldDef[]): StepDef => ({
  key,
  title,
  info,
  fields,
});

const chapter = (key: string, title: string, steps: StepDef[]): ChapterDef => ({
  key,
  title,
  steps,
});

const collectFlow = (
  key: FlowKey,
  role: CollectRole,
  title: string,
  chapters: ChapterDef[],
): FlowDef => ({
  key,
  title,
  callToAction: "Give your agents what they need",
  collect: { role },
  chapters,
});

// The founder and CEO --------------------------------------------------------------------------

export const COLLECT_CEO = collectFlow("collect_ceo", "ceo", "CEO collection", [
  chapter("offer", "Offer & prices", [
    step("services", "Services and prices", "What the company sells and for how much.", [
      q({
        key: "ceo_services",
        label: "Services and packages",
        info: "Deal Desk builds proposals only from this list; Matchmaker and Chief of Staff route requests by it.",
        type: "list",
        usedBy: ["Deal Desk", "Matchmaker", "Chief of Staff", "Marketing Director"],
        required: true,
        placeholder: "One per line: package, hours, rate",
      }),
      q({
        key: "ceo_price_list",
        label: "Price list",
        info: "Deal Desk quotes from it and flags any off-list price for your approval.",
        type: "files",
        usedBy: ["Deal Desk", "Refund Desk", "Finance Tracker"],
        required: true,
      }),
      q({
        key: "ceo_included",
        label: "What's included",
        info: "So proposals and outreach never promise more than the package holds.",
        type: "longtext",
        usedBy: ["Deal Desk", "Outreach Runner"],
      }),
    ]),
    step("contract", "Contract and refunds", "The terms every placement runs on.", [
      q({
        key: "ceo_contract",
        label: "Contract template",
        info: "Deal Desk attaches it to proposals; Refund Desk cites its clauses in every case.",
        type: "files",
        usedBy: ["Deal Desk", "Refund Desk", "Onboarding Runner"],
        required: true,
      }),
      q({
        key: "ceo_refund_policy",
        label: "Refund policy",
        info: "When a refund is due and how much: Refund Desk recommends from it, Andrei decides.",
        type: "longtext",
        usedBy: ["Refund Desk", "Finance Tracker", "Account Health"],
        required: true,
        decision: true,
      }),
      q({
        key: "ceo_replacement",
        label: "Replacement guarantee",
        info: "Account Health starts a replacement only within these terms.",
        type: "longtext",
        usedBy: ["Account Health", "Refund Desk", "Deal Desk"],
        decision: true,
      }),
    ]),
  ]),
  chapter("certification", "Certification", [
    step("criteria", "Certification criteria", "What a VA must prove before placement.", [
      q({
        key: "ceo_cert_criteria",
        label: "Certification criteria",
        info: "The 5–10 things a VA must prove. Trainer grades against them; Matchmaker places only certified VAs.",
        type: "list",
        usedBy: ["Trainer", "Screener", "Matchmaker"],
        required: true,
        decision: true,
      }),
    ]),
  ]),
  chapter("clients", "Clients & outreach", [
    step("ideal_client", "Ideal client", "Who the company sells to.", [
      q({
        key: "ceo_ideal_client",
        label: "Ideal client",
        info: "Leads Bro qualifies leads against it; Outreach Runner and Deal Desk pitch to it.",
        type: "longtext",
        usedBy: ["Leads Bro", "Outreach Runner", "Deal Desk"],
      }),
      q({
        key: "ceo_outreach_templates",
        label: "Approved outreach templates",
        info: "Outreach Runner drafts only from templates you approved.",
        type: "files",
        usedBy: ["Outreach Runner", "Salesperson"],
      }),
    ]),
  ]),
  chapter("brand", "Brand & likeness", [
    step("voice", "Voice", "How you sound.", [
      q({
        key: "ceo_voice_rules",
        label: "Voice do's and don'ts",
        info: "The copy room writes and judges every post against it.",
        type: "longtext",
        usedBy: ["Copy room", "Marketing Director", "Art Director"],
        decision: true,
      }),
      q({
        key: "ceo_brand_refs",
        label: "Brand references",
        info: "Links to looks you like: the Art Director casts styles from them.",
        type: "links",
        usedBy: ["Art Director", "Design room"],
      }),
    ]),
    step("likeness", "Likeness", "Your photos for images of you.", [
      q({
        key: "ceo_photos",
        label: "Reference photos",
        info: "15–30 photos of you. The design room's likeness check compares every portrait to them.",
        type: "files",
        usedBy: ["Design room", "Art Director"],
        required: true,
      }),
      q({
        key: "ceo_likeness_use",
        label: "Likeness use",
        info: "How freely the design room may put your face in a design.",
        type: "select",
        usedBy: ["Design room", "Art Director", "Marketing Director"],
        decision: true,
        options: [
          { value: "approve_each", label: "Approve every image" },
          { value: "approved_styles", label: "Approved styles only" },
          { value: "any", label: "Any style" },
        ],
      }),
    ]),
  ]),
  chapter("approvals", "Approvals", [
    step("approval_style", "Approval style", "How much the agents do without you.", [
      q({
        key: "ceo_approval_mode",
        label: "Approval style",
        info: "Chief of Staff sets every agent's approval queue from it.",
        type: "select",
        usedBy: ["Chief of Staff", "Marketing Director", "Support"],
        required: true,
        decision: true,
        options: [
          { value: "approve_everything", label: "Approve everything" },
          { value: "approve_risky", label: "Approve money, brand and clients" },
          { value: "autonomous", label: "Autonomous, report after" },
        ],
      }),
      q({
        key: "ceo_approves_what",
        label: "Who approves what",
        info: "Chief of Staff routes each card type to its approver from this.",
        type: "longtext",
        usedBy: ["Chief of Staff"],
        decision: true,
      }),
    ]),
    step("priorities", "Priorities", "What matters this quarter.", [
      q({
        key: "ceo_priorities",
        label: "Priorities",
        info: "Chief of Staff ranks the board and the daily status by them.",
        type: "list",
        usedBy: ["Chief of Staff", "Marketing Director"],
      }),
      q({
        key: "ceo_weekly_targets",
        label: "Weekly targets",
        info: "The numbers the weekly KPI report holds each department to.",
        type: "longtext",
        usedBy: ["Chief of Staff", "Marketing Director"],
      }),
    ]),
  ]),
]);

// Account managers -----------------------------------------------------------------------------

export const COLLECT_AM = collectFlow("collect_am", "am", "Account manager collection", [
  chapter("clients", "Clients", [
    step("placements", "Clients and placements", "Every account you own.", [
      q({
        key: "am_clients",
        label: "Your clients",
        info: "Account Health watches every one of them; your assistant sorts mail by them.",
        type: "list",
        usedBy: ["Account Health", "AM Assistant", "Check-in Runner"],
        required: true,
        placeholder: "One per line: client, VA, hours",
      }),
      q({
        key: "am_placements_export",
        label: "Active placements export",
        info: "Client, VA, hours, rate and start date per placement: Account Health and Finance Tracker start from it.",
        type: "files",
        usedBy: ["Account Health", "Finance Tracker", "Check-in Runner"],
        required: true,
      }),
      q({
        key: "am_fulfillment_lead",
        label: "Fulfillment Lead",
        info: "Whether you lead the account managers: escalations from Account Health go to the lead first.",
        type: "boolean",
        usedBy: ["Chief of Staff", "Account Health"],
      }),
    ]),
  ]),
  chapter("checkins", "Check-ins", [
    step("cadence", "Check-in cadence", "How often each client hears from you.", [
      q({
        key: "am_checkin_cadence",
        label: "Check-in cadence",
        info: "Check-in Runner schedules and drafts check-ins on it.",
        type: "select",
        usedBy: ["Check-in Runner", "Account Health"],
        required: true,
        options: [
          { value: "day_7_30_90", label: "Day 7, 30, 90" },
          { value: "weekly", label: "Weekly" },
          { value: "biweekly", label: "Every two weeks" },
          { value: "monthly", label: "Monthly" },
        ],
      }),
      q({
        key: "am_checkin_questions",
        label: "Check-in questions",
        info: "What Check-in Runner asks the client and the VA each time.",
        type: "list",
        usedBy: ["Check-in Runner"],
      }),
      q({
        key: "am_qa_template",
        label: "QA note template",
        info: "Check-in Runner writes every QA note in this shape.",
        type: "files",
        usedBy: ["Check-in Runner"],
      }),
    ]),
  ]),
  chapter("health", "Account health", [
    step("signals", "Health signals", "What tells you an account is at risk.", [
      q({
        key: "am_risk_signals",
        label: "Risk signals",
        info: "Account Health raises a risk flag when it sees one of these.",
        type: "multiselect",
        usedBy: ["Account Health"],
        required: true,
        options: [
          { value: "silence", label: "Client goes silent" },
          { value: "hours_drop", label: "Hours drop" },
          { value: "missed_checkin", label: "Missed check-in" },
          { value: "late_reports", label: "Late VA reports" },
          { value: "client_complaint", label: "Client complaint" },
          { value: "va_complaint", label: "VA complaint" },
          { value: "invoice_late", label: "Invoice paid late" },
        ],
      }),
      q({
        key: "am_silence_days",
        label: "Silent days before a flag",
        info: "How many days without a word from the client before Account Health flags it.",
        type: "number",
        usedBy: ["Account Health"],
        min: 1,
        max: 90,
      }),
      q({
        key: "am_replacement_steps",
        label: "Replacement steps",
        info: "What happens when a VA must be replaced: Account Health and Matchmaker follow it.",
        type: "longtext",
        usedBy: ["Account Health", "Matchmaker"],
        decision: true,
      }),
      q({
        key: "am_escalation",
        label: "When to come to you",
        info: "What your assistant and Account Health bring to you at once instead of in the daily summary.",
        type: "longtext",
        usedBy: ["Account Health", "AM Assistant"],
        decision: true,
      }),
    ]),
  ]),
  chapter("onboarding", "Onboarding", [
    step("checklist", "Onboarding", "What a new placement needs in week one.", [
      q({
        key: "am_onboarding_checklist",
        label: "Onboarding checklist",
        info: "Onboarding Runner opens each placement with these items, owned and dated.",
        type: "list",
        usedBy: ["Onboarding Runner"],
      }),
      q({
        key: "am_onboarding_templates",
        label: "Welcome, contract and NDA templates",
        info: "Onboarding Runner drafts the welcome and paperwork from them.",
        type: "files",
        usedBy: ["Onboarding Runner"],
      }),
    ]),
  ]),
]);

// Recruiter ------------------------------------------------------------------------------------

export const COLLECT_RECRUITER = collectFlow(
  "collect_recruiter",
  "recruiter",
  "Recruiter collection",
  [
    chapter("posting", "Posting map", [
      step("groups", "Groups and boards", "Everywhere you post jobs.", [
        q({
          key: "rec_fb_groups",
          label: "Facebook groups",
          info: "Sourcer posts only in these groups, with your yes per post.",
          type: "links",
          usedBy: ["Sourcer", "Recruiter's Assistant"],
          required: true,
        }),
        q({
          key: "rec_job_boards",
          label: "Job boards",
          info: "The other places Sourcer posts and searches.",
          type: "links",
          usedBy: ["Sourcer"],
        }),
        q({
          key: "rec_posting_rules",
          label: "Group rules and cadence",
          info: "Each group's rules and how often you post there: Sourcer never breaks them.",
          type: "longtext",
          usedBy: ["Sourcer"],
        }),
        q({
          key: "rec_account_owner",
          label: "Posting accounts",
          info: "Which account posts in each group, so Sourcer drafts for the right one.",
          type: "text",
          usedBy: ["Sourcer"],
        }),
      ]),
    ]),
    chapter("posts", "Job posts", [
      step("designs", "Past job posts", "What worked before.", [
        q({
          key: "rec_past_posts",
          label: "Past job-post designs",
          info: "10 or more past posts: Marketing designs new job posts in the same line.",
          type: "files",
          usedBy: ["Sourcer", "Art Director", "Design room", "Marketing Director"],
          required: true,
        }),
        q({
          key: "rec_best_posts",
          label: "Best 3 and why",
          info: "Which posts brought the best candidates, and why: the copy and design rooms learn from it.",
          type: "longtext",
          usedBy: ["Art Director", "Copy room", "Sourcer"],
        }),
        q({
          key: "rec_post_template",
          label: "Job-post copy template",
          info: "The copy room fills it for every new request.",
          type: "longtext",
          usedBy: ["Sourcer", "Copy room"],
        }),
      ]),
    ]),
    chapter("screening", "Screening", [
      step("profiles", "Candidate profiles", "Who fits each role type.", [
        q({
          key: "rec_role_profiles",
          label: "Profiles per role type",
          info: "EA, bookkeeper, SDR…: what a good candidate looks like for each. Sourcer and Matchmaker search by it.",
          type: "list",
          usedBy: ["Sourcer", "Screener", "Matchmaker"],
        }),
      ]),
      step("criteria", "Screening criteria", "How you decide who goes on.", [
        q({
          key: "rec_screening_criteria",
          label: "Screening criteria",
          info: "Screener passes or stops a candidate on these.",
          type: "list",
          usedBy: ["Screener"],
          required: true,
          decision: true,
        }),
        q({
          key: "rec_tests",
          label: "Tests you run",
          info: "Screener sends and scores these.",
          type: "multiselect",
          usedBy: ["Screener"],
          options: [
            { value: "english", label: "English" },
            { value: "typing", label: "Typing" },
            { value: "tools", label: "Tools" },
            { value: "assessment", label: "Skills assessment" },
            { value: "trial_task", label: "Trial task" },
          ],
        }),
        q({
          key: "rec_red_flags",
          label: "Red flags",
          info: "Anything here stops a candidate and tells you why.",
          type: "list",
          usedBy: ["Screener", "Recruiter's Assistant"],
        }),
        q({
          key: "rec_scoring",
          label: "Scoring and pass mark",
          info: "How Screener turns test results into a pass or a no.",
          type: "longtext",
          usedBy: ["Screener"],
          decision: true,
        }),
        q({
          key: "rec_candidate_messages",
          label: "Candidate message templates",
          info: "Your assistant and Sourcer draft candidate messages from them.",
          type: "longtext",
          usedBy: ["Sourcer", "Recruiter's Assistant"],
        }),
      ]),
    ]),
    chapter("training", "Training & pool", [
      step("curriculum", "Training", "How VAs are trained and graded.", [
        q({
          key: "rec_curriculum",
          label: "Training curriculum",
          info: "Trainer builds each VA's checklist from it.",
          type: "files",
          usedBy: ["Trainer"],
        }),
        q({
          key: "rec_modules",
          label: "Modules and length",
          info: "Trainer tracks progress module by module.",
          type: "list",
          usedBy: ["Trainer"],
        }),
        q({
          key: "rec_grading",
          label: "Grading",
          info: "How Trainer grades a module before it marks a VA certified (with your yes).",
          type: "longtext",
          usedBy: ["Trainer"],
          decision: true,
        }),
      ]),
      step("pool", "Certified pool", "Who is ready to place now.", [
        q({
          key: "rec_pool_export",
          label: "Certified pool export",
          info: "Skills, availability, rate and time zone per VA: Matchmaker shortlists from it.",
          type: "files",
          usedBy: ["Matchmaker", "Account Health"],
        }),
      ]),
    ]),
  ],
);

// Salesperson ----------------------------------------------------------------------------------

export const COLLECT_SALES = collectFlow("collect_sales", "sales", "Sales collection", [
  chapter("offer", "Offer & pricing", [
    step("offer", "Offer", "What you sell and what you may give.", [
      q({
        key: "sales_offer",
        label: "Offer",
        info: "The offer in your words: Deal Desk briefs calls from it, Outreach Runner pitches it.",
        type: "longtext",
        usedBy: ["Deal Desk", "Outreach Runner", "Leads Bro"],
        required: true,
      }),
      q({
        key: "sales_pricing",
        label: "Pricing you may give",
        info: "Discounts and terms you may agree alone; anything else Deal Desk sends to the CEO.",
        type: "longtext",
        usedBy: ["Deal Desk"],
        required: true,
        decision: true,
      }),
      q({
        key: "sales_proposal_template",
        label: "Proposal template",
        info: "Deal Desk drafts every proposal in it.",
        type: "files",
        usedBy: ["Deal Desk"],
      }),
    ]),
  ]),
  chapter("calls", "Calls", [
    step("discovery", "Discovery", "How a first call runs.", [
      q({
        key: "sales_discovery",
        label: "Discovery script",
        info: "Deal Desk preps each call brief around it.",
        type: "longtext",
        usedBy: ["Deal Desk", "Sales Assistant"],
      }),
      q({
        key: "sales_qualifying",
        label: "Qualifying questions",
        info: "What makes a lead worth a call: Leads Bro and Deal Desk check them.",
        type: "list",
        usedBy: ["Deal Desk", "Leads Bro"],
      }),
      q({
        key: "sales_objections",
        label: "Objections and answers",
        info: "Deal Desk and Outreach Runner answer objections only from this list.",
        type: "list",
        usedBy: ["Deal Desk", "Outreach Runner"],
        required: true,
        placeholder: "One per line: objection — answer",
      }),
    ]),
  ]),
  chapter("pipeline", "Pipeline", [
    step("stages", "Stages", "How a deal moves.", [
      q({
        key: "sales_stages",
        label: "Pipeline stages",
        info: "Deal Desk reads and moves deals by these stages.",
        type: "list",
        usedBy: ["Deal Desk", "Outreach Runner"],
      }),
      q({
        key: "sales_stale_days",
        label: "Days until a deal is stale",
        info: "Deal Desk's daily check flags deals untouched this long.",
        type: "number",
        usedBy: ["Deal Desk"],
        min: 1,
        max: 180,
      }),
      q({
        key: "sales_warm_reply",
        label: "Warm reply",
        info: "Which replies Outreach Runner hands you as a warm card.",
        type: "longtext",
        usedBy: ["Outreach Runner"],
      }),
      q({
        key: "sales_send_approval",
        label: "Outreach sends",
        info: "How much of Outreach Runner's sending you approve.",
        type: "select",
        usedBy: ["Outreach Runner"],
        decision: true,
        options: [
          { value: "approve_each", label: "Approve each send" },
          { value: "approve_batches", label: "Approve each batch" },
          { value: "templates_free", label: "Approved templates go out alone" },
        ],
      }),
    ]),
  ]),
  chapter("deals", "Past deals", [
    step("won_lost", "Won and lost", "What closed and what did not.", [
      q({
        key: "sales_won",
        label: "Won proposals",
        info: "Deal Desk models new proposals on them.",
        type: "files",
        usedBy: ["Deal Desk"],
      }),
      q({
        key: "sales_lost",
        label: "Lost proposals",
        info: "So Deal Desk avoids what lost.",
        type: "files",
        usedBy: ["Deal Desk"],
      }),
      q({
        key: "sales_deal_lessons",
        label: "Why won or lost",
        info: "The reasons, in a few lines: Deal Desk and Marketing learn from them.",
        type: "longtext",
        usedBy: ["Deal Desk", "Marketing Director"],
      }),
    ]),
  ]),
]);

// Andrei: finance, refunds, ops ----------------------------------------------------------------

export const COLLECT_FINANCE = collectFlow(
  "collect_finance",
  "finance",
  "Finance & ops collection",
  [
    chapter("refunds", "Refunds", [
      step("rules", "Refund rules", "How refunds are decided.", [
        q({
          key: "fin_refund_rules",
          label: "Refund rules",
          info: "Refund Desk builds every case against them; no money moves without your yes.",
          type: "longtext",
          usedBy: ["Refund Desk", "Finance Tracker"],
          required: true,
          decision: true,
        }),
        q({
          key: "fin_refund_limit",
          label: "Refunds you must approve above",
          info: "An amount (USD) above which a refund always waits for you; below it an account manager may agree.",
          type: "money",
          usedBy: ["Refund Desk", "Account Health"],
          decision: true,
        }),
        q({
          key: "fin_past_refunds",
          label: "Past refund decisions",
          info: "Refund Desk cites similar past cases.",
          type: "files",
          usedBy: ["Refund Desk"],
        }),
      ]),
    ]),
    chapter("budgets", "Budgets", [
      step("de_budgets", "DE budgets", "What each DE may spend.", [
        q({
          key: "fin_de_budgets",
          label: "Budget per DE",
          info: "A monthly amount per DE: Finance Tracker and Support watch spend against it.",
          type: "list",
          usedBy: ["Finance Tracker", "Support", "Chief of Staff"],
          required: true,
          decision: true,
          placeholder: "One per line: DE — amount per month",
        }),
        q({
          key: "fin_over_budget",
          label: "When a DE hits its budget",
          info: "What happens at the limit.",
          type: "select",
          usedBy: ["Finance Tracker", "Support"],
          decision: true,
          options: [
            { value: "stop", label: "Stop it" },
            { value: "ask", label: "Ask me first" },
            { value: "alert", label: "Alert only" },
          ],
        }),
      ]),
    ]),
    chapter("money", "Money in and out", [
      step("invoices", "Invoices and payouts", "How money moves.", [
        q({
          key: "fin_invoicing_tool",
          label: "Invoicing tool",
          info: "Where Finance Tracker reads invoices.",
          type: "text",
          usedBy: ["Finance Tracker"],
        }),
        q({
          key: "fin_payouts",
          label: "Payout schedule and VA pay",
          info: "Finance Tracker builds the payout ledger from it.",
          type: "longtext",
          usedBy: ["Finance Tracker"],
        }),
        q({
          key: "fin_margin",
          label: "Margin formula",
          info: "How margin per placement is computed in the weekly report.",
          type: "text",
          usedBy: ["Finance Tracker"],
        }),
        q({
          key: "fin_collections",
          label: "Overdue and collections rules",
          info: "When an invoice counts as overdue and what reminders go out.",
          type: "longtext",
          usedBy: ["Collections", "Finance Tracker"],
          decision: true,
        }),
      ]),
    ]),
    chapter("ops", "Ops & platform", [
      step("tools", "Tools and site", "The accounts and systems agents touch.", [
        q({
          key: "fin_tool_accounts",
          label: "Tool accounts",
          info: "Every tool account the company holds and its owner.",
          type: "list",
          usedBy: ["Admin Desk", "Support"],
        }),
        q({
          key: "fin_site_links",
          label: "Site repo and analytics",
          info: "The site's repo, deploy path and PostHog projects: Site Keeper reports and proposes changes from them.",
          type: "links",
          usedBy: ["Site Keeper"],
        }),
        q({
          key: "fin_skillsmith",
          label: "Support report",
          info: "Who gets the weekly support report, on which day and channel.",
          type: "text",
          usedBy: ["Support"],
        }),
      ]),
    ]),
  ],
);

export const COLLECT_FLOWS: readonly FlowDef[] = [
  COLLECT_CEO,
  COLLECT_AM,
  COLLECT_RECRUITER,
  COLLECT_SALES,
  COLLECT_FINANCE,
];

export const isCollectFlow = (key: string): boolean => key.startsWith("collect_");

/** The fields of a collection flow, in order. */
export const collectFields = (def: FlowDef): FieldDef[] =>
  allSteps(def).flatMap((s) => [...s.fields]);

/** The roles that read a field (empty for a field that is not collected knowledge). */
export const usedByOf = (f: FieldDef): readonly string[] =>
  f.bind.kind === "collect" ? f.bind.usedBy : [];

export type CollectMissing = {
  step: string;
  field: string;
  label: string;
  required: boolean;
  usedBy: readonly string[];
};

export type CollectProgress = {
  total: number;
  answered: number;
  /** Whole percent of the questions answered. */
  percent: number;
  /** The required questions answered, of all required ones. */
  requiredAnswered: number;
  requiredTotal: number;
  missing: CollectMissing[];
};

/** How much of one person's collection is answered, and what is still missing (in flow order). */
export function collectProgress(def: FlowDef, answers: Answers): CollectProgress {
  let total = 0;
  let answered = 0;
  let requiredTotal = 0;
  let requiredAnswered = 0;
  const missing: CollectMissing[] = [];
  for (const s of allSteps(def))
    for (const f of s.fields) {
      if (f.bind.kind !== "collect") continue;
      total += 1;
      if (f.required) requiredTotal += 1;
      if (isAnswered(f, answers[f.key])) {
        answered += 1;
        if (f.required) requiredAnswered += 1;
      } else
        missing.push({
          step: s.key,
          field: f.key,
          label: f.label,
          required: Boolean(f.required),
          usedBy: f.bind.usedBy,
        });
    }
  return {
    total,
    answered,
    percent: total === 0 ? 100 : Math.round((answered / total) * 100),
    requiredAnswered,
    requiredTotal,
    missing,
  };
}

/** One collection as the role view counts it: a flow sent to a seat, or none sent yet. */
export type CollectSource = {
  def: FlowDef;
  /** The seat it was sent about (null: no session sent for this person-role yet). */
  seatId: string | null;
  person: string | null;
  answers: Answers;
};

export type RoleReadiness = "have" | "partial" | "missing";

export type RoleNeed = {
  role: string;
  total: number;
  answered: number;
  /** `have`: everything in; `missing`: nothing yet; else `partial` (the readiness page's states). */
  status: RoleReadiness;
  missing: {
    flow: FlowKey;
    field: string;
    label: string;
    seatId: string | null;
    person: string | null;
    required: boolean;
  }[];
};

/**
 * What each role on the work chart still waits for, over every collection: each question counts
 * once per seat it was sent to (three account managers each owe their clients), and a
 * person-role with no session sent counts its questions as missing. Roles in name order.
 */
export function roleNeeds(sources: readonly CollectSource[]): RoleNeed[] {
  const byRole = new Map<string, RoleNeed>();
  const covered = new Set(sources.map((s) => s.def.key));
  const all: CollectSource[] = [
    ...sources,
    ...COLLECT_FLOWS.filter((d) => !covered.has(d.key)).map((def) => ({
      def,
      seatId: null,
      person: null,
      answers: {},
    })),
  ];
  for (const src of all)
    for (const f of collectFields(src.def)) {
      if (f.bind.kind !== "collect") continue;
      const ok = isAnswered(f, src.answers[f.key]);
      for (const role of f.bind.usedBy) {
        const need = byRole.get(role) ?? {
          role,
          total: 0,
          answered: 0,
          status: "missing" as RoleReadiness,
          missing: [],
        };
        need.total += 1;
        if (ok) need.answered += 1;
        else
          need.missing.push({
            flow: src.def.key,
            field: f.key,
            label: f.label,
            seatId: src.seatId,
            person: src.person,
            required: Boolean(f.required),
          });
        byRole.set(role, need);
      }
    }
  for (const need of byRole.values())
    need.status =
      need.answered === need.total ? "have" : need.answered === 0 ? "missing" : "partial";
  return [...byRole.values()].sort((a, b) => (a.role < b.role ? -1 : a.role > b.role ? 1 : 0));
}

/** An answer as plain text for the knowledge doc and the Recall record (files by name). */
export function answerText(
  f: FieldDef,
  v: AnswerValue | undefined,
  fileName: (id: string) => string = (id) => id,
): string {
  if (v === null || v === undefined) return "";
  if (f.type === "boolean") return v === true ? "Yes" : "No";
  if (f.type === "select" && typeof v === "string")
    return f.options?.find((o) => o.value === v)?.label ?? v;
  if (Array.isArray(v)) {
    const labels =
      f.type === "multiselect"
        ? v.map((x) => f.options?.find((o) => o.value === x)?.label ?? x)
        : f.type === "files"
          ? v.map(fileName)
          : v;
    return labels.map((x) => `- ${x}`).join("\n");
  }
  return String(v);
}
